import type { TableColumnsType } from 'antd';
import {
  Card,
  Switch,
  Tabs,
  Row,
  Col,
  Space,
  message,
  Typography,
  Table,
  Button,
  Modal,
  Tag,
  Descriptions,
  Dropdown,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  EditOutlined,
  UndoOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  MoreOutlined,
  QuestionCircleOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { RuleTestModal } from '~/components/ruleSets/RuleTestModal';
import { AssetsActionBar } from '../../../components/partnerConfiguration/AssetsActionBar';
import { useStoreState, useStoreActions } from '../../../store';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PC_GLOBAL_TENANT_ID } from '../../../store/partnerConfiguration/types';
import { BaseFeatureToggles } from '../../../store/navigation/BaseFeatureToggles';
import { ReadmeIcon } from '../../../components/assets/ReadmeIcon';
import { buildUpdateRulePayload } from './utils';
import { filterTableData } from '../../../components/workflow/shared/TableUtils';
import type {
  PartnerRule,
  PartnerRuleCriterion,
  PartnerRuleUpdate,
  PartnerTenant,
} from '@shipium-corp/shipium-ts-types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom-v5-compat';
import { useSession } from '~/hooks/useSession';
import { OPT_INS } from '~/store/security/OptIns';
import { createUseStyles } from 'react-jss';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useMutationInvalidator } from '~/hooks/useMutationInvalidator';
import { partnerManagementService } from '~/services';
import { RuleImpactCountButton } from '~/components/ruleSets/RuleImpactCountButton';
import { DescribeRuleButton } from '~/components/ruleSets/DescribeRuleButton';
import type { NamedRef } from '~/services/mastra-service';
import { ruleCriteriaToFilter } from '~/utils/ruleImpactCount/ruleCriteriaToTypesenseFilter';

const useStyles = createUseStyles(() => ({
  table: {
    '& .ant-table-expanded-row .ant-table-cell': {
      backgroundColor: 'AppWorkspace',
    },
  },
  expandedRow: {
    marginLeft: 48,
  },
  container: {
    width: '100%',
  },
}));

const SHIPIUM_CARRIER_RULES_DOCUMENTATION_URL = 'https://docs.shipium.com/docs/carrier-selection-rules';

const searchTermList = [
  { term: 'Name', description: 'Search by name of the rule' },
  { term: 'Description', description: 'Search by the rule description text' },
  {
    term: 'Destination Matching',
    description: 'Search for a postal code that you have configured in destination matching rule',
  },
];

type RuleEntry = {
  key: string;
  ruleId: string;
  rule?: PartnerRule;
  orderDisplay: number;
  postalCodes: string[];
  tenants: PartnerTenant[];
};

export const RuleSetPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { sessionPartnerUrl, isOptInEnabled, isShipiumView } = useSession();
  // The Rules Reforge tooling (rule test, impact counts, and the AI describe/draft
  // features) is gated behind the RULES_AI opt-in. Internal Shipium users always see it.
  const ruleToolsEnabled = isShipiumView || isOptInEnabled(OPT_INS.RULES_AI);
  const [searchBarText, setSearchBarText] = useState('');
  const [activeTabKey, setActiveTabKey] = useState('active');
  const [ruleTestModalOpen, setRuleTestModalOpen] = useState(false);

  const {
    data: ruleSet,
    refetch: refetchRuleSet,
    isLoading: isRuleSetLoading,
  } = useQuery({
    ...useQueryCreator(partnerManagementService.ruleSets.partnerRuleSet, {
      args: [],
    }),
    enabled: Boolean(sessionPartnerUrl),
  });

  const {
    data: rules,
    refetch: refetchRules,
    isLoading: isRulesLoading,
  } = useQuery({
    ...useQueryCreator(partnerManagementService.rules.search, {
      args: [],
    }),
    enabled: Boolean(sessionPartnerUrl),
  });

  const { data: origins } = useQuery({
    ...useQueryCreator(partnerManagementService.origins.search, {
      args: [],
    }),
    enabled: Boolean(sessionPartnerUrl),
  });

  const updateRule = useMutation(
    useMutationInvalidator(async ({ ruleId, payload }: { ruleId: string; payload: Partial<PartnerRuleUpdate> }) => {
      const rule = await partnerManagementService.rules.get.fn(ruleId);

      return partnerManagementService.rules.update({
        ruleId,
        payload: { ...buildUpdateRulePayload(rule), ...payload },
      });
    }),
    {
      onSuccess: () => {
        message.success('Rule updated successfully');
      },
      onError: (error) => {
        console.error(error);
        message.error('Failed to update rule');
      },
      onSettled: () => {
        refetchRules();
      },
    },
  );

  const archiveRule = useMutation(
    useMutationInvalidator(async (ruleId: string) => {
      if (!ruleSet?.ruleSetId) {
        return { invalidateQueryKeys: [] };
      }

      return await partnerManagementService.ruleSets.archiveRule({
        ruleId,
        ruleSetId: ruleSet.ruleSetId,
      });
    }),
    {
      onSuccess: () => {
        message.success('Rule updated successfully');
      },
      onError: (error) => {
        console.error(error);
        message.error('Failed to update rule');
      },
      onSettled: () => {
        refetchRuleSet();
        refetchRules();
      },
    },
  );

  const restoreRule = useMutation(
    useMutationInvalidator(async (ruleId: string) => {
      if (!ruleSet?.ruleSetId) {
        return { invalidateQueryKeys: [] };
      }

      return partnerManagementService.ruleSets.restoreRule({
        ruleId,
        ruleSetId: ruleSet.ruleSetId,
      });
    }),
    {
      onSuccess: () => {
        message.success('Rule updated successfully');
      },
      onError: (error) => {
        console.error(error);
        message.error('Failed to update rule');
      },
      onSettled: () => {
        refetchRuleSet();
        refetchRules();
      },
    },
  );

  const updateRulePriority = useMutation(
    useMutationInvalidator(async ({ ruleId, priorityAmount }: { ruleId: string; priorityAmount: number }) => {
      if (!ruleSet?.ruleSetId) {
        return { invalidateQueryKeys: [] };
      }

      return partnerManagementService.ruleSets.updateRulePriority({
        ruleId,
        ruleSetId: ruleSet.ruleSetId,
        priorityAmount,
      });
    }),
    {
      onSuccess: () => {
        message.success('Rule updated successfully');
      },
      onError: (error) => {
        console.error(error);
        message.error('Failed to update rule');
      },
      onSettled: () => {
        refetchRuleSet();
      },
    },
  );

  //#region Tenant Initialization
  const fetchTenants = useStoreActions((actions) => actions.tenant.fetchTenants);
  const tenants = useStoreState((state) => state.tenant.tenants);
  const [tenantId, setTenantId] = useState(PC_GLOBAL_TENANT_ID);
  const usesTenant =
    useStoreState((state) => state.partner.activePartner)?.featureToggles[BaseFeatureToggles.TENANT] === 'true';
  useEffect(() => {
    if (usesTenant) fetchTenants({});
  }, [fetchTenants, usesTenant]);
  //#endregion

  //#region AI Describe — carrier/tenant name maps for DescribeRuleButton.
  // Computed once at the page level and passed to each button, so we don't
  // trigger a per-row fetch (every button mounting concurrently would queue
  // N redundant store actions on first render).
  const fetchUsedV2Carriers = useStoreActions((actions) => actions.carrier.fetchUsedV2Carriers);
  const usedV2Carriers = useStoreState((state) => state.carrier.usedV2Carriers);
  // Tracking the fetch with a ref instead of inferring from the store value:
  // partners with zero used carriers get an empty {} back, and using
  // Object.keys(...).length === 0 as a "not yet fetched" signal would loop
  // forever (fetch → empty result → effect re-fires → fetch again).
  const usedV2CarriersFetched = useRef(false);
  useEffect(() => {
    if (ruleToolsEnabled && !usedV2CarriersFetched.current) {
      usedV2CarriersFetched.current = true;
      fetchUsedV2Carriers();
    }
  }, [ruleToolsEnabled, fetchUsedV2Carriers]);

  const describeTenantNames = useMemo<NamedRef[]>(
    () =>
      Object.values(tenants ?? {})
        .filter((t) => t.tenantId && t.name)
        .map((t) => ({ id: t.tenantId!, name: t.name! })),
    [tenants],
  );

  const describeCarrierNames = useMemo<NamedRef[]>(() => {
    const refs: NamedRef[] = [];
    for (const [carrierId, carrier] of Object.entries(usedV2Carriers ?? {})) {
      if (carrier?.name) {
        refs.push({ id: carrierId, name: carrier.name });
      }
      for (const [methodId, method] of Object.entries(carrier?.methods ?? {})) {
        if (method?.name) {
          refs.push({ id: methodId, name: method.name });
        }
      }
    }
    return refs;
  }, [usedV2Carriers]);
  //#endregion

  const { activeRules, inactiveRules } = useMemo<{ activeRules: RuleEntry[]; inactiveRules: RuleEntry[] }>(() => {
    if (!ruleSet || !rules) {
      return { activeRules: [], inactiveRules: [] };
    }

    const activeRuleMap = new Set(ruleSet.rules.map(({ ruleId }) => ruleId));

    const filterByTenant = (entry: { tenants: PartnerTenant[] }) => {
      if (tenantId === PC_GLOBAL_TENANT_ID || entry.tenants.length === 0) {
        return true;
      }

      return entry.tenants.some((tenant) => tenant.tenantId === tenantId);
    };

    const mapTenants = (tenantIds: string[] | undefined) =>
      tenantIds?.filter((id) => tenants[id]).map((id) => tenants[id]) || [];

    const getPostalCodes = (criteria: PartnerRuleCriterion[] | undefined): string[] => {
      if (!criteria) {
        return [];
      }

      return (
        criteria
          .filter((c) => c?.ruleProperty?.rulePropertyEnum === 'destination_postal')
          .flatMap((c) => c?.compareValue as unknown as string) || []
      );
    };

    const activeRules =
      ruleSet?.rules
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
        .map((entry, index) => {
          const rule = rules.find((r) => r.ruleId === entry.ruleId);

          return {
            key: entry.ruleId!,
            ruleId: entry.ruleId!,
            orderDisplay: index + 1,
            postalCodes: getPostalCodes(rule?.criteria),
            tenants: mapTenants(rule?.tenantIds),
            rule: rule!,
            name: rule?.name,
            description: rule?.description,
          };
        })
        .filter(filterByTenant) || [];

    const inactiveRules =
      Object.values(rules || {})
        .filter((rule) => !activeRuleMap.has(rule.ruleId))
        .map((rule) => {
          return {
            key: rule.ruleId!,
            ruleId: rule.ruleId!,
            orderDisplay: 0,
            postalCodes: getPostalCodes(rule.criteria),
            tenants: mapTenants(rule.tenantIds),
            rule: rule,
            name: rule.name,
            description: rule.description,
          };
        })
        .filter(filterByTenant) || [];

    return { activeRules, inactiveRules };
  }, [rules, tenants, tenantId, ruleSet]);

  const handleTabSelection = (activeKey: string) => {
    setActiveTabKey(activeKey);
  };

  const isLoading =
    updateRule.isLoading ||
    archiveRule.isLoading ||
    restoreRule.isLoading ||
    updateRulePriority.isLoading ||
    isRuleSetLoading ||
    isRulesLoading;

  const isEmpty = activeRules.length === 0;

  const activeRulesColumns: TableColumnsType<RuleEntry> = [
    {
      title: 'Rule Details',
      key: 'ruleDetails',
      render: (value: RuleEntry) => (
        <Space direction="vertical" size={0}>
          <Space size={0}>
            <Typography.Text strong>
              {value.orderDisplay}. {value.rule?.name}
            </Typography.Text>
          </Space>
          <Typography.Text type="secondary">{value.rule?.description}</Typography.Text>
          <Space size={0} wrap>
            {value.tenants.map((tenant) => (
              <Tag key={tenant.tenantId}>{tenant.name}</Tag>
            ))}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Enabled',
      key: 'settings',
      width: 72,
      render: (value: RuleEntry) => {
        return (
          <Switch
            disabled={isLoading}
            checked={value.rule?.active}
            checkedChildren={'On'}
            unCheckedChildren={'Off'}
            onChange={() => {
              updateRule.mutate({
                ruleId: value.ruleId,
                payload: { active: !value.rule?.active },
              });
            }}
          />
        );
      },
    },
    {
      title: (
        <Space>
          <Typography.Text style={{ whiteSpace: 'pre-line' }}>Continue{'\n'}Processing</Typography.Text>
          <Tooltip title="When selecting 'Continue' for a rule, even if the rule's conditions are met, the next rule will still be evaluated. Otherwise, no additional rules will be processed.">
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      ),
      key: 'continueProcessing',
      width: 112,
      render: (value: RuleEntry) => {
        return (
          <Switch
            loading={isLoading}
            checked={!value.rule?.terminating}
            checkedChildren={'Continue'}
            unCheckedChildren={'Stop'}
            onChange={() => {
              updateRule.mutate({
                ruleId: value.ruleId,
                payload: { terminating: !value.rule?.terminating },
              });
            }}
          />
        );
      },
    },
    {
      key: 'checkImpact',
      width: 130,
      render: (value: RuleEntry) =>
        ruleToolsEnabled && value.rule ? (
          <RuleImpactCountButton
            getFilterResult={() =>
              ruleCriteriaToFilter(value.rule!.criteria, value.rule!.tenantIds, value.rule!.carrierAndServiceMethodList)
            }
            listType={value.rule!.listType}
          />
        ) : null,
    },
    {
      key: 'describeRule',
      width: 120,
      render: (value: RuleEntry) =>
        ruleToolsEnabled && value.rule ? (
          <DescribeRuleButton rule={value.rule} carrierNames={describeCarrierNames} tenantNames={describeTenantNames} />
        ) : null,
    },
    {
      key: 'actions',
      width: 48,
      render: (value: RuleEntry) => (
        <Dropdown
          menu={{
            items: [
              {
                disabled: value.orderDisplay === 1 || isLoading,
                key: 'increaseOrder',
                label: 'Increase Order',
                icon: <ArrowUpOutlined />,
                onClick: () => {
                  updateRulePriority.mutate({
                    ruleId: value.ruleId,
                    priorityAmount: 1,
                  });
                },
              },
              {
                disabled: value.orderDisplay === activeRules.length || isLoading,
                key: 'decreaseOrder',
                label: 'Decrease Order',
                icon: <ArrowDownOutlined />,
                onClick: () => {
                  updateRulePriority.mutate({
                    ruleId: value.ruleId,
                    priorityAmount: -1,
                  });
                },
              },
              {
                key: 'edit',
                label: 'Edit',
                icon: <EditOutlined />,
                onClick: () => {
                  navigate(
                    `/${sessionPartnerUrl}/carrier-selection/rulesets/${ruleSet?.ruleSetId}/rules/${value.ruleId}`,
                  );
                },
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: <DeleteOutlined />,
                disabled: isLoading,
                onClick: () => {
                  Modal.confirm({
                    title: 'Make Inactive',
                    content: `Are you sure you want to make this rule inactive - (${value.rule?.name})?`,
                    onOk() {
                      archiveRule.mutate(value.ruleId);
                    },
                  });
                },
              },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ].filter(({ key }) => usesTenant || key !== 'tenant');

  const inactiveRulesColumns: TableColumnsType<RuleEntry> = [
    {
      title: 'Rule Details',
      key: 'ruleDetails',
      render: (value: RuleEntry) => (
        <Space direction="vertical" size={0}>
          <Space size={0}>
            <Typography.Text strong>
              {value.orderDisplay}. {value.rule?.name}
            </Typography.Text>
          </Space>
          <Typography.Text type="secondary">{value.rule?.description}</Typography.Text>
          <Space size={0} wrap>
            {value.tenants.map((tenant) => (
              <Tag key={tenant.tenantId}>{tenant.name}</Tag>
            ))}
          </Space>
        </Space>
      ),
    },
    {
      key: 'checkImpact',
      width: 130,
      render: (value: RuleEntry) =>
        ruleToolsEnabled && value.rule ? (
          <RuleImpactCountButton
            getFilterResult={() =>
              ruleCriteriaToFilter(value.rule!.criteria, value.rule!.tenantIds, value.rule!.carrierAndServiceMethodList)
            }
            listType={value.rule!.listType}
          />
        ) : null,
    },
    {
      key: 'describeRule',
      width: 120,
      render: (value: RuleEntry) =>
        ruleToolsEnabled && value.rule ? (
          <DescribeRuleButton rule={value.rule} carrierNames={describeCarrierNames} tenantNames={describeTenantNames} />
        ) : null,
    },
    {
      key: 'actions',
      width: 48,
      render: (value: RuleEntry) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Edit',
                icon: <EditOutlined />,
                onClick: () => {
                  navigate(
                    `/${sessionPartnerUrl}/carrier-selection/rulesets/${ruleSet?.ruleSetId}/rules/${value.ruleId}`,
                  );
                },
              },
              {
                key: 'restore',
                label: 'Restore',
                icon: <UndoOutlined />,
                onClick: () => {
                  Modal.confirm({
                    title: 'Make Active',
                    content: `Completing this action will make this rule (${value.rule?.name}) active. Newly active rules are not enabled by default; until enabled, this rule will not be considered in carrier selection requests. Would you like to make this rule active?`,
                    onOk() {
                      restoreRule.mutate(value.ruleId);
                    },
                  });
                },
              },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const filteredActiveRules = filterTableData(activeRules, searchBarText);
  const filteredInactiveRules = filterTableData(inactiveRules, searchBarText);
  const hiddenActiveCount = (activeRules?.length ?? 0) - (filteredActiveRules?.length ?? 0);
  const hiddenInactiveCount = (inactiveRules?.length ?? 0) - (filteredInactiveRules?.length ?? 0);

  const searchHelp = (
    <div>
      <ul>
        {searchTermList.map((sh) => (
          <li key={sh.term}>
            <b>{sh.term}</b>: <span>{sh.description}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const expandedRowRender = (record: RuleEntry) => {
    return (
      <Descriptions size="small" bordered className={classes.expandedRow}>
        {record.rule?.criteria.map((criterion) => {
          const values = criterion.compareValue;
          const maxArrayLength = 20;
          const overflow = Array.isArray(values) ? values.length - maxArrayLength : 0;

          return (
            <Descriptions.Item
              labelStyle={{ width: 300 }}
              contentStyle={{ backgroundColor: 'AppWorkspace' }}
              label={
                <Typography.Text>
                  {criterion.ruleProperty.rulePropertyName}{' '}
                  <Typography.Text strong>{criterion.ruleComparator.ruleComparatorName}</Typography.Text>
                </Typography.Text>
              }
              key={criterion.ruleProperty.rulePropertyEnum}
              span={3}
            >
              {Array.isArray(values) ? (
                values.slice(0, maxArrayLength).map((value) => {
                  if (criterion.ruleProperty.rulePropertyEnum === 'origin') {
                    const origin = origins?.find((origin) => origin.originId === value);
                    return <Tag key={value}>{origin?.name ?? value}</Tag>;
                  }

                  return <Tag key={value}>{value}</Tag>;
                })
              ) : (
                <Typography.Text>
                  {String(values)} {criterion.scalarUnit?.scalarUnitName ?? ''}
                </Typography.Text>
              )}
              {overflow > 0 && <Tag>{`+${overflow} more`}</Tag>}
            </Descriptions.Item>
          );
        })}
        <Descriptions.Item
          contentStyle={{ backgroundColor: 'AppWorkspace' }}
          label={
            <Typography.Text>
              Carrier & Service Methods{' '}
              {record.rule?.listType === 'add' && <Typography.Text strong>Add</Typography.Text>}
              {record.rule?.listType === 'block' && <Typography.Text strong>Remove</Typography.Text>}
              {record.rule?.listType === 'allow' && <Typography.Text strong>Use Only</Typography.Text>}
            </Typography.Text>
          }
          span={3}
        >
          {record.rule?.carrierAndServiceMethodList.map((carrierAndServiceMethod) => (
            <Tag key={carrierAndServiceMethod}>{carrierAndServiceMethod}</Tag>
          ))}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  return (
    <Space direction="vertical" className={classes.container}>
      <AssetsActionBar
        setSearchBarValue={setSearchBarText}
        tenantId={tenantId}
        setTenantId={setTenantId}
        pageActions={[
          ...(ruleToolsEnabled
            ? [
                {
                  title: 'Test Rules',
                  icon: <ExperimentOutlined />,
                  onClick: () => setRuleTestModalOpen(true),
                },
              ]
            : []),
          {
            title: 'Add Rule',
            icon: <PlusOutlined />,
            onClick: () => {
              if (!ruleSet) {
                return;
              }

              navigate(`/${sessionPartnerUrl}/carrier-selection/rulesets/${ruleSet.ruleSetId}/rules/`);
            },
          },
        ]}
        searchHelp={searchHelp}
        searchPlaceholder="Search Rules"
      />
      <Card loading={isEmpty && isLoading}>
        <Row>
          <Col span={8}>
            <Typography.Title level={5}>Carrier Selection Rules</Typography.Title>
          </Col>
          <Col offset={12} span={4} style={{ textAlign: 'right' }} title={'Open Shipium Documentation'}>
            <Button
              type="link"
              rel="noreferrer"
              icon={<ReadmeIcon style={{ fontSize: '20px' }} />}
              target="_blank"
              href={SHIPIUM_CARRIER_RULES_DOCUMENTATION_URL}
            />
          </Col>
        </Row>
        <Tabs
          items={[
            {
              key: 'active',
              disabled: isLoading,
              label: (
                <>
                  <Tag>{activeRules.length}</Tag>
                  Active Rules
                </>
              ),
              children: (
                <>
                  {!!hiddenActiveCount && <i>{hiddenActiveCount} active rules hidden due to search </i>}
                  <Table
                    className={classes.table}
                    size="small"
                    columns={activeRulesColumns}
                    dataSource={filteredActiveRules}
                    pagination={false}
                    expandable={{
                      expandedRowRender,
                      expandIcon: ({ expanded, onExpand, record }) => (
                        <Button
                          size="small"
                          icon={expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
                          onClick={(e) => onExpand(record, e)}
                        />
                      ),
                    }}
                  />
                </>
              ),
            },
            {
              key: 'inactive',
              disabled: isLoading,
              label: (
                <>
                  <Tag>{inactiveRules.length}</Tag>
                  Inactive Rules
                </>
              ),
              children: (
                <>
                  {!!hiddenInactiveCount && <i> {hiddenInactiveCount} inactive rules hidden due to search </i>}
                  <Table
                    className={classes.table}
                    size="small"
                    columns={inactiveRulesColumns}
                    dataSource={filteredInactiveRules}
                    pagination={false}
                    expandable={{
                      expandedRowRender,
                      expandIcon: ({ expanded, onExpand, record }) => (
                        <Button
                          size="small"
                          icon={expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
                          onClick={(e) => onExpand(record, e)}
                        />
                      ),
                    }}
                  />
                </>
              ),
            },
          ]}
          onTabClick={handleTabSelection}
          type={'card'}
          activeKey={activeTabKey}
        />
      </Card>
      {ruleToolsEnabled && <RuleTestModal open={ruleTestModalOpen} onClose={() => setRuleTestModalOpen(false)} />}
    </Space>
  );
};

export default RuleSetPage;
